import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Lenis from "@studio-freight/lenis";

import PageTransition from "./components/utils/PageTransition";
import Navbar from "./components/layout/Navbar/Navbar";
import Footer from "./components/layout/Footer/Footer";
import WhatsAppButton from "./components/whatsApp/WhatsAppButton";

import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Gallery from "./pages/Gallery";
import Blog from "./pages/Blog";
import Expeditions from "./pages/Expeditions";
import Trekkings from "./pages/Trekkings";
import ExpeditionGear from "./pages/ExpeditionGear";
import Trainings from "./pages/Trainings";
import TermsOfService from "./pages/TermsOfServices";
import TrainingForms from "./pages/TrainingForm";

import "./App.css";

/* ============================
   Disable Browser Scroll Restore
============================ */
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/* ============================
   Animated Routes
============================ */
function AnimatedRoutes({ lenis }) {
  const location = useLocation();

  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={() => {
        if (lenis) {
          lenis.scrollTo(0, { immediate: true });
        } else {
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      }}
    >
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/adventures/expeditions" element={<PageTransition><Expeditions /></PageTransition>} />
        <Route path="/adventures/trekking" element={<PageTransition><Trekkings /></PageTransition>} />
        <Route path="/adventures/training-programs" element={<PageTransition><Trainings /></PageTransition>} />
        <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
        <Route path="/blogs" element={<PageTransition><Blog /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/gallery" element={<PageTransition><Gallery /></PageTransition>} />
        <Route path="/expedition-gear" element={<PageTransition><ExpeditionGear /></PageTransition>} />
        <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
        <Route path="/academy/join" element={<PageTransition><TrainingForms /></PageTransition>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

/* ============================
   Layout Controller
============================ */
function Layout({ children }) {
  const location = useLocation();
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  const hideLayoutRoutes = ["/academy/join"];
  const hideLayout = hideLayoutRoutes.includes(location.pathname);

  /* ============================
     WhatsApp Visibility (ALL PAGES)
     Appears after 100vh scroll
  ============================ */
  useEffect(() => {
    setShowWhatsApp(false);

    const handleScroll = () => {
      const triggerPoint = window.innerHeight; // 100vh
      setShowWhatsApp(window.scrollY > triggerPoint);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  return (
   <div className="bg-black text-white min-h-screen flex flex-col">
  {!hideLayout && <Navbar />}

  <div className="flex-1">{children}</div>

  {!hideLayout && <Footer />}
  {!hideLayout && showWhatsApp && <WhatsAppButton />}
</div>

  );
}

/* ============================
   App Root
============================ */
function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });

    // 🚀 Force scroll to TOP on refresh
    requestAnimationFrame(() => {
      lenisRef.current.scrollTo(0, { immediate: true });
    });

    function raf(time) {
      lenisRef.current.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    const handleAnchorClick = (e) => {
      const target = e.target.closest("a[href^='#']");
      if (target) {
        e.preventDefault();
        const id = target.getAttribute("href");
        const el = document.querySelector(id);
        if (el) {
          lenisRef.current.scrollTo(el);
        }
      }
    };

    document.addEventListener("click", handleAnchorClick);

    return () => {
      document.removeEventListener("click", handleAnchorClick);
      lenisRef.current?.destroy();
    };
  }, []);

  return (
    <Router>
      <Layout>
        <AnimatedRoutes lenis={lenisRef.current} />
      </Layout>
    </Router>
  );
}

export default App;
